import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import RestHttpClient from '../../../../common/RestHttpClient';
import {
  CalculationMatchOverview,
  ConditionDebugRuleView,
  GetMatchingCampaignResponse,
  RuleSetOverview,
} from '../../../../generated/ApiClient';

// Konditionslogik-Debug: zeigt pro Kalkulation die gematchten Merkmale und die Auswertung jedes
// Kandidaten-RuleSets bis auf Einzelregel-Ebene. Live-Auswertung auf den persistierten Merkmalen,
// es wird nichts mitgeschrieben.

// Feste Spaltenbreiten, damit die Regeltabellen aller Konditionen untereinander buendig sind -
// ohne tableLayout 'fixed' richtet sich jede Tabelle nach ihrem eigenen Inhalt aus.
const RULE_COLUMN_WIDTHS = ['46%', '18%', '18%', '18%'];

export function ConditionDebug({ dealId }: { dealId: number }) {
  const { t } = useTranslation();
  const [includeSimulation, setIncludeSimulation] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [initialised, setInitialised] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['classification', 'conditionDebug', dealId, includeSimulation],
    queryFn: () => RestHttpClient.getMatchingRuleSets(dealId, includeSimulation),
    select: (x) => x.data,
  });

  const allKeys = useMemo(() => collectKeys(data), [data]);

  // Grundzustand: Kalkulationen offen, Merkmale und Konditionen zu - bei 60+ Konditionen ist
  // alles offen unbrauchbar.
  const effectiveOpenKeys = useMemo(() => {
    if (initialised || !data) return openKeys;
    return data.calculations.map((c) => calcKey(c));
  }, [initialised, data, openKeys]);

  const isOpen = (key: string) => effectiveOpenKeys.includes(key);

  const toggle = (key: string) => {
    setInitialised(true);
    setOpenKeys(
      isOpen(key) ? effectiveOpenKeys.filter((k) => k !== key) : [...effectiveOpenKeys, key]
    );
  };

  const allExpanded = allKeys.length > 0 && allKeys.every((k) => effectiveOpenKeys.includes(k));

  const toggleAll = () => {
    setInitialised(true);
    setOpenKeys(allExpanded ? [] : allKeys);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return <Alert severity="error">{t('deals.edit.conditionDebug.loadError')}</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0 }}>{t('deals.edit.conditionDebug.title')}</h2>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button variant="outlined" size="small" onClick={toggleAll} disabled={!allKeys.length}>
            {allExpanded
              ? t('deals.edit.conditionDebug.collapseAll')
              : t('deals.edit.conditionDebug.expandAll')}
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={includeSimulation}
                onChange={(e) => setIncludeSimulation(e.target.checked)}
              />
            }
            label={t('deals.edit.conditionDebug.includeSimulation')}
          />
        </Box>
      </Box>

      <Alert severity="info">{t('deals.edit.conditionDebug.liveHint')}</Alert>

      {!data.useCampaign && (
        <Alert severity="warning">{t('deals.edit.conditionDebug.useCampaignOff')}</Alert>
      )}

      {data.calculations.length === 0 && (
        <Alert severity="info">{t('deals.edit.conditionDebug.noCalculations')}</Alert>
      )}

      {data.calculations.map((calculation) => (
        <CalculationSection
          key={calculation.calculationId}
          calculation={calculation}
          isOpen={isOpen}
          onToggle={toggle}
        />
      ))}
    </Stack>
  );
}

function CalculationSection({
  calculation,
  isOpen,
  onToggle,
}: {
  calculation: CalculationMatchOverview;
  isOpen: (key: string) => boolean;
  onToggle: (key: string) => void;
}) {
  const { t } = useTranslation();
  const matchedCount = calculation.ruleSets.filter((r) => r.matched).length;

  return (
    <Accordion
      disableGutters
      expanded={isOpen(calcKey(calculation))}
      onChange={() => onToggle(calcKey(calculation))}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">
            {t('deals.edit.conditionDebug.calculation', {
              nr: calculation.calculationNr || calculation.calculationId,
            })}
          </Typography>
          <Chip
            size="small"
            color={matchedCount > 0 ? 'success' : 'default'}
            label={t('deals.edit.conditionDebug.matchedCount', {
              matched: matchedCount,
              total: calculation.ruleSets.length,
            })}
          />
          <Typography variant="caption" color="text.secondary">
            {t('deals.edit.conditionDebug.matchDate', {
              date: new Date(calculation.matchDate).toLocaleDateString('de-AT'),
            })}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1}>
          <Accordion
            disableGutters
            variant="outlined"
            expanded={isOpen(featuresKey(calculation))}
            onChange={() => onToggle(featuresKey(calculation))}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                {t('deals.edit.conditionDebug.matchedFeatures')} ({calculation.features.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: RULE_COLUMN_WIDTHS[0] }}>
                      {t('classification.features.name')}
                    </TableCell>
                    <TableCell sx={{ width: RULE_COLUMN_WIDTHS[1] }}>
                      {t('classification.features.value')}
                    </TableCell>
                    <TableCell sx={{ width: RULE_COLUMN_WIDTHS[2] }}>
                      {t('deals.edit.conditionDebug.source')}
                    </TableCell>
                    <TableCell sx={{ width: RULE_COLUMN_WIDTHS[3] }}>
                      {t('classification.features.isOverwritten')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculation.features.map((feature) => (
                    <TableRow key={feature.name}>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{feature.name}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{feature.value || '—'}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{feature.source}</TableCell>
                      <TableCell>{feature.isOverwritten ? t('yes') : t('no')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>

          {calculation.ruleSets.map((ruleSet) => (
            <RuleSetSection
              key={ruleSet.id}
              ruleSet={ruleSet}
              open={isOpen(ruleSetKey(calculation, ruleSet))}
              onToggle={() => onToggle(ruleSetKey(calculation, ruleSet))}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function RuleSetSection({
  ruleSet,
  open,
  onToggle,
}: {
  ruleSet: RuleSetOverview;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Accordion
      disableGutters
      variant="outlined"
      expanded={open}
      onChange={onToggle}
      // Gruen = greift. Ersetzt den frueheren "greift"-Chip.
      sx={{
        borderLeft: 4,
        borderLeftColor: ruleSet.matched ? 'success.main' : 'divider',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: ruleSet.matched ? 600 : 400 }}>
            {ruleSet.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatValidity(ruleSet, t)}
          </Typography>
          {ruleSet.error && (
            <Chip size="small" color="error" label={t('deals.edit.conditionDebug.hasError')} />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {ruleSet.error && <Alert severity="error">{ruleSet.error}</Alert>}

        {ruleSet.rules.length > 0 && (
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: RULE_COLUMN_WIDTHS[0] }}>
                  {t('deals.edit.conditionDebug.rule')}
                </TableCell>
                <TableCell sx={{ width: RULE_COLUMN_WIDTHS[1] }}>
                  {t('deals.edit.conditionDebug.expected')}
                </TableCell>
                <TableCell sx={{ width: RULE_COLUMN_WIDTHS[2] }}>
                  {t('deals.edit.conditionDebug.actual')}
                </TableCell>
                <TableCell sx={{ width: RULE_COLUMN_WIDTHS[3] }}>
                  {t('deals.edit.conditionDebug.result')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ruleSet.rules.map((rule, index) => (
                <RuleRow key={index} rule={rule} />
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function RuleRow({ rule }: { rule: ConditionDebugRuleView }) {
  const { t } = useTranslation();
  const isGroup = !rule.field;

  return (
    <TableRow>
      <TableCell sx={{ pl: 1 + rule.depth * 3, wordBreak: 'break-word' }}>
        {rule.not && <Chip size="small" label="NOT" sx={{ mr: 0.5 }} />}
        {isGroup ? (
          <strong>{rule.combinator?.toUpperCase()}</strong>
        ) : (
          <>
            {rule.field} <em>{rule.operator}</em>
          </>
        )}
      </TableCell>
      <TableCell sx={{ wordBreak: 'break-word' }}>
        {isGroup ? '' : rule.expectedValue || '—'}
      </TableCell>
      <TableCell sx={{ wordBreak: 'break-word' }}>
        {isGroup ? (
          ''
        ) : rule.featureFound ? (
          rule.actualValue || '—'
        ) : (
          <Typography variant="body2" color="error.main">
            {t('deals.edit.conditionDebug.featureMissing')}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ wordBreak: 'break-word' }}>
        {rule.error ? (
          <Typography variant="body2" color="error.main">
            {rule.error}
          </Typography>
        ) : (
          <Chip
            size="small"
            color={rule.matched ? 'success' : 'default'}
            label={rule.matched ? t('yes') : t('no')}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

const calcKey = (calculation: CalculationMatchOverview) => `calc-${calculation.calculationId}`;
const featuresKey = (calculation: CalculationMatchOverview) =>
  `features-${calculation.calculationId}`;
const ruleSetKey = (calculation: CalculationMatchOverview, ruleSet: RuleSetOverview) =>
  `rs-${calculation.calculationId}-${ruleSet.id}`;

function collectKeys(data?: GetMatchingCampaignResponse) {
  if (!data) return [];

  return data.calculations.flatMap((calculation) => [
    calcKey(calculation),
    featuresKey(calculation),
    ...calculation.ruleSets.map((ruleSet) => ruleSetKey(calculation, ruleSet)),
  ]);
}

function formatValidity(ruleSet: RuleSetOverview, t: (key: string) => string) {
  const from = ruleSet.validFrom
    ? new Date(ruleSet.validFrom).toLocaleDateString('de-AT')
    : t('deals.edit.conditionDebug.unlimited');
  const to = ruleSet.validTo
    ? new Date(ruleSet.validTo).toLocaleDateString('de-AT')
    : t('deals.edit.conditionDebug.unlimited');

  return `${from} – ${to}`;
}
